import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { RotateCcw, MousePointerClick } from 'lucide-react';
import { useTranslation } from '../../../i18n';

const shuffleWithSeed = (array, seed) => {
  const result = [...array];
  const seededRandom = (s) => {
    const x = Math.sin(s) * 10000;
    return x - Math.floor(x);
  };
  let seedValue = seed;
  for (let i = result.length - 1; i > 0; i--) {
    seedValue++;
    const j = Math.floor(seededRandom(seedValue) * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

// Labels-on-blank-canvas exercise: teacher places slots at arbitrary positions
// on a plain canvas (no image), and students drag labels from a side palette
// onto those slots. Same content shape as dragLabelImageExercise (slots + labels
// + paletteSide + correctLabelId), so it shares the classroom check handler.
const DragLabelBlankElement = ({
  element,
  isEditor = false,
  studentView = false,
  placements: externalPlacements = null,
  showResult = false,
  perSlotResults = null,
  onPlacementsChange,
  onCheck,
  onRetry,
  disabled = false,
  hideCheckButton = false,
  updateElementContent,
  saveToHistory,
}) => {
  const { t } = useTranslation();
  const { content, id } = element;
  const {
    slots = [],
    labels = [],
    paletteSide = 'left',
    allowRetry = false,
    slotColor = '#2A4D25',
    labelColor = '#ffffff',
    labelTextColor = '#1f2937',
    fontFamily = 'Inter',
    fontSize = 14,
    paletteColor = '#f9fafb',
  } = content || {};

  // Palette placement — free position + size in % of the slide (which is also
  // the exercise widget bounds since it spans 100×100). Falls back to a
  // paletteSide-derived default for older exercises without saved coords.
  const paletteDefaults = (() => {
    switch (paletteSide) {
      case 'top':    return { position: { x: 20, y: 3 },  size: { width: 60, height: 12 } };
      case 'bottom': return { position: { x: 20, y: 85 }, size: { width: 60, height: 12 } };
      case 'right':  return { position: { x: 82, y: 20 }, size: { width: 15, height: 60 } };
      case 'left':
      default:       return { position: { x: 3, y: 20 },  size: { width: 15, height: 60 } };
    }
  })();
  const palettePos = content?.palettePosition || paletteDefaults.position;
  const paletteBoxSize = content?.paletteSize || paletteDefaults.size;
  const isHorizontalPalette = paletteBoxSize.width >= paletteBoxSize.height;

  const shuffledLabels = useMemo(() => {
    if (!labels || labels.length === 0) return [];
    const seed = id ? id.split('').reduce((a, c) => a + c.charCodeAt(0), 0) : 1;
    return shuffleWithSeed(labels, seed);
  }, [labels, id]);

  const labelMinWidth = useMemo(() => {
    if (!labels || labels.length === 0) return 80;
    if (typeof document === 'undefined') {
      const longest = labels.reduce((m, l) => Math.max(m, (l?.text || '').length), 0);
      return Math.max(80, Math.round(longest * fontSize * 0.55) + 28);
    }
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = `500 ${fontSize}px "${fontFamily}", Inter, system-ui, sans-serif`;
    let maxW = 0;
    labels.forEach((l) => {
      const w = ctx.measureText(l?.text || '').width;
      if (w > maxW) maxW = w;
    });
    return Math.max(80, Math.ceil(maxW) + 32);
  }, [labels, fontFamily, fontSize]);

  // placements: { [slotId]: labelId | null }
  const [placements, setPlacements] = useState(externalPlacements || {});
  useEffect(() => {
    if (externalPlacements) setPlacements(externalPlacements);
  }, [externalPlacements]);

  const [draggedLabelId, setDraggedLabelId] = useState(null);
  const [dragOverSlotId, setDragOverSlotId] = useState(null);
  // Touch/pen drag state — HTML5 drag events do not fire on mobile browsers,
  // so we run a parallel pointer-based drag with a floating ghost element and
  // elementFromPoint hit-testing to find the target slot.
  const pointerDragRef = useRef(null);

  const placedLabelIds = useMemo(
    () => new Set(Object.values(placements).filter(Boolean)),
    [placements]
  );
  const allPlaced =
    !isEditor && shuffledLabels.length > 0 && shuffledLabels.every((l) => placedLabelIds.has(l.id));

  const updatePlacements = useCallback(
    (next) => {
      setPlacements(next);
      onPlacementsChange?.(next);
    },
    [onPlacementsChange]
  );

  const handleDragStart = (e, labelId) => {
    if (disabled || showResult || isEditor) return;
    try { e.dataTransfer.setData('text/plain', labelId); } catch (_) {}
    e.dataTransfer.effectAllowed = 'move';

    // Safari can't render a drag image when the source lives inside a
    // `transform: scale(...)` subtree (the slide is rendered scaled). Attach an
    // off-screen ghost on document.body so WebKit has something to draw.
    const label = labels.find((l) => l.id === labelId);
    if (label && typeof document !== 'undefined' && e.dataTransfer.setDragImage) {
      const ghost = document.createElement('div');
      ghost.textContent = label.text;
      Object.assign(ghost.style, {
        position: 'fixed',
        top: '-1000px',
        left: '-1000px',
        padding: '4px 12px',
        borderRadius: '9999px',
        border: `2px solid ${slotColor}`,
        background: labelColor,
        color: labelTextColor,
        font: `500 ${fontSize}px "${fontFamily}", Inter, system-ui, sans-serif`,
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      });
      document.body.appendChild(ghost);
      const rect = ghost.getBoundingClientRect();
      e.dataTransfer.setDragImage(ghost, rect.width / 2, rect.height / 2);
      setTimeout(() => { ghost.remove(); }, 0);
    }

    setDraggedLabelId(labelId);
  };

  const handleDragEnd = () => {
    setDraggedLabelId(null);
    setDragOverSlotId(null);
  };

  const handleSlotDragOver = (e, slotId) => {
    if (disabled || showResult || isEditor) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverSlotId(slotId);
  };

  const handleSlotDragLeave = () => setDragOverSlotId(null);

  const handleSlotDrop = (e, slotId) => {
    if (disabled || showResult || isEditor) return;
    e.preventDefault();
    const labelId = draggedLabelId || e.dataTransfer.getData('text/plain');
    if (!labelId) return;
    const next = { ...placements };
    Object.keys(next).forEach((sid) => {
      if (next[sid] === labelId) next[sid] = null;
    });
    next[slotId] = labelId;
    updatePlacements(next);
    setDraggedLabelId(null);
    setDragOverSlotId(null);
  };

  const handlePlacedClick = (slotId) => {
    if (disabled || showResult || isEditor) return;
    if (!placements[slotId]) return;
    const next = { ...placements };
    next[slotId] = null;
    updatePlacements(next);
  };

  // Start a pointer-based drag for touch/pen input. Mouse still uses native
  // HTML5 drag so we don't regress desktop behavior.
  const handlePointerDown = useCallback(
    (e, labelId) => {
      if (disabled || showResult || isEditor) return;
      if (e.pointerType === 'mouse') return;
      if (pointerDragRef.current) return;
      const label = labels.find((l) => l.id === labelId);
      if (!label) return;
      e.preventDefault();

      const ghost = document.createElement('div');
      ghost.textContent = label.text;
      Object.assign(ghost.style, {
        position: 'fixed',
        left: '0',
        top: '0',
        padding: '4px 12px',
        borderRadius: '9999px',
        border: `2px solid ${slotColor}`,
        background: labelColor,
        color: labelTextColor,
        font: `500 ${fontSize}px "${fontFamily}", Inter, system-ui, sans-serif`,
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
        boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
        zIndex: '99999',
        opacity: '0.95',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        willChange: 'transform',
      });
      document.body.appendChild(ghost);
      const rect = ghost.getBoundingClientRect();
      ghost.style.transform = `translate(${e.clientX - rect.width / 2}px, ${e.clientY - rect.height / 2}px)`;

      pointerDragRef.current = { labelId, ghostEl: ghost, pointerId: e.pointerId };
      setDraggedLabelId(labelId);
    },
    [disabled, showResult, isEditor, labels, slotColor, labelColor, labelTextColor, fontFamily, fontSize]
  );

  useEffect(() => {
    const findSlotIdAt = (clientX, clientY) => {
      const drag = pointerDragRef.current;
      if (!drag) return null;
      const prevDisplay = drag.ghostEl.style.display;
      drag.ghostEl.style.display = 'none';
      const el = typeof document !== 'undefined' ? document.elementFromPoint(clientX, clientY) : null;
      drag.ghostEl.style.display = prevDisplay;
      const slotEl = el && el.closest ? el.closest('[data-drag-slot-id]') : null;
      return slotEl ? slotEl.getAttribute('data-drag-slot-id') : null;
    };

    const cleanupDrag = () => {
      const drag = pointerDragRef.current;
      if (!drag) return;
      if (drag.ghostEl && drag.ghostEl.parentNode) drag.ghostEl.parentNode.removeChild(drag.ghostEl);
      pointerDragRef.current = null;
      setDraggedLabelId(null);
      setDragOverSlotId(null);
    };

    const onMove = (e) => {
      const drag = pointerDragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      e.preventDefault();
      const rect = drag.ghostEl.getBoundingClientRect();
      drag.ghostEl.style.transform = `translate(${e.clientX - rect.width / 2}px, ${e.clientY - rect.height / 2}px)`;
      setDragOverSlotId(findSlotIdAt(e.clientX, e.clientY));
    };

    const onUp = (e) => {
      const drag = pointerDragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      const slotId = findSlotIdAt(e.clientX, e.clientY);
      const labelId = drag.labelId;
      cleanupDrag();
      if (slotId) {
        const next = { ...placements };
        Object.keys(next).forEach((sid) => {
          if (next[sid] === labelId) next[sid] = null;
        });
        next[slotId] = labelId;
        updatePlacements(next);
      }
    };

    const onCancel = (e) => {
      const drag = pointerDragRef.current;
      if (!drag) return;
      if (e && e.pointerId != null && e.pointerId !== drag.pointerId) return;
      cleanupDrag();
    };

    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
  }, [placements, updatePlacements]);

  useEffect(
    () => () => {
      const drag = pointerDragRef.current;
      if (drag && drag.ghostEl && drag.ghostEl.parentNode) {
        drag.ghostEl.parentNode.removeChild(drag.ghostEl);
      }
      pointerDragRef.current = null;
    },
    []
  );

  const handleCheck = () => onCheck?.(placements);
  const handleRetry = () => {
    setPlacements({});
    onRetry?.();
  };

  const allSlotsFilled = slots.length > 0 && slots.every((s) => placements[s.id]);

  // Palette drag / resize (editor-only). Palette coords are stored in
  // content.palettePosition (top-left) and content.paletteSize, both as % of
  // slide. Widget spans 100×100 so its bounding rect === slide rect.
  const rootRef = useRef(null);
  const [paletteInteract, setPaletteInteract] = useState(null);
  // Slot drag / resize (editor-only). Slots are stored as { x, y, width, height }
  // where (x, y) is the CENTER in % of slide (rendered with translate(-50%, -50%)).
  const [slotInteract, setSlotInteract] = useState(null);

  const beginPaletteInteract = (e, mode) => {
    if (!isEditor || !updateElementContent) return;
    if (!rootRef.current) return;
    // Resize handles must not bubble into the outer palette body (which would
    // overwrite our resize mode with a drag). Drag mode intentionally bubbles
    // up so the wrapper can still trigger element selection.
    if (mode.startsWith('resize-')) e.stopPropagation();
    e.preventDefault();
    const rect = rootRef.current.getBoundingClientRect();
    setPaletteInteract({
      mode,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startPos: { ...palettePos },
      startSize: { ...paletteBoxSize },
      slideWidth: rect.width,
      slideHeight: rect.height,
      moved: false,
    });
  };

  useEffect(() => {
    if (!paletteInteract || !updateElementContent) return;
    const MIN_W = 6;
    const MIN_H = 4;
    const onMove = (e) => {
      const dxPct = ((e.clientX - paletteInteract.startMouseX) / paletteInteract.slideWidth) * 100;
      const dyPct = ((e.clientY - paletteInteract.startMouseY) / paletteInteract.slideHeight) * 100;
      let nextPos = { ...paletteInteract.startPos };
      let nextSize = { ...paletteInteract.startSize };
      if (paletteInteract.mode === 'drag') {
        nextPos.x = Math.max(0, Math.min(100 - nextSize.width, paletteInteract.startPos.x + dxPct));
        nextPos.y = Math.max(0, Math.min(100 - nextSize.height, paletteInteract.startPos.y + dyPct));
      } else if (paletteInteract.mode === 'resize-se') {
        nextSize.width = Math.max(MIN_W, Math.min(100 - paletteInteract.startPos.x, paletteInteract.startSize.width + dxPct));
        nextSize.height = Math.max(MIN_H, Math.min(100 - paletteInteract.startPos.y, paletteInteract.startSize.height + dyPct));
      } else if (paletteInteract.mode === 'resize-nw') {
        const rightEdge = paletteInteract.startPos.x + paletteInteract.startSize.width;
        const bottomEdge = paletteInteract.startPos.y + paletteInteract.startSize.height;
        nextPos.x = Math.max(0, Math.min(rightEdge - MIN_W, paletteInteract.startPos.x + dxPct));
        nextPos.y = Math.max(0, Math.min(bottomEdge - MIN_H, paletteInteract.startPos.y + dyPct));
        nextSize.width = rightEdge - nextPos.x;
        nextSize.height = bottomEdge - nextPos.y;
      } else if (paletteInteract.mode === 'resize-ne') {
        const bottomEdge = paletteInteract.startPos.y + paletteInteract.startSize.height;
        nextSize.width = Math.max(MIN_W, Math.min(100 - paletteInteract.startPos.x, paletteInteract.startSize.width + dxPct));
        nextPos.y = Math.max(0, Math.min(bottomEdge - MIN_H, paletteInteract.startPos.y + dyPct));
        nextSize.height = bottomEdge - nextPos.y;
      } else if (paletteInteract.mode === 'resize-sw') {
        const rightEdge = paletteInteract.startPos.x + paletteInteract.startSize.width;
        nextPos.x = Math.max(0, Math.min(rightEdge - MIN_W, paletteInteract.startPos.x + dxPct));
        nextSize.width = rightEdge - nextPos.x;
        nextSize.height = Math.max(MIN_H, Math.min(100 - paletteInteract.startPos.y, paletteInteract.startSize.height + dyPct));
      }
      updateElementContent({
        ...content,
        palettePosition: { x: nextPos.x, y: nextPos.y },
        paletteSize: { width: nextSize.width, height: nextSize.height },
      });
      if (!paletteInteract.moved && (Math.abs(dxPct) > 0.2 || Math.abs(dyPct) > 0.2)) {
        setPaletteInteract((s) => (s ? { ...s, moved: true } : s));
      }
    };
    const onUp = () => {
      if (paletteInteract.moved) saveToHistory?.();
      setPaletteInteract(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [paletteInteract, content, updateElementContent, saveToHistory]);

  const beginSlotInteract = (e, mode, slotId) => {
    if (!isEditor || !updateElementContent) return;
    if (!rootRef.current) return;
    // Resize handles must not bubble into the slot body (which would overwrite
    // our resize mode with a drag). Drag mode intentionally bubbles up so the
    // wrapper can still trigger element selection.
    if (mode.startsWith('resize-')) e.stopPropagation();
    e.preventDefault();
    const slot = slots.find((s) => s.id === slotId);
    if (!slot) return;
    const rect = rootRef.current.getBoundingClientRect();
    setSlotInteract({
      slotId,
      mode,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startX: slot.x,
      startY: slot.y,
      startWidth: slot.width || 18,
      startHeight: slot.height || 10,
      slideWidth: rect.width,
      slideHeight: rect.height,
      moved: false,
    });
  };

  useEffect(() => {
    if (!slotInteract || !updateElementContent) return;
    const MIN_W = 6;
    const MIN_H = 4;
    const onMove = (e) => {
      const dxPct = ((e.clientX - slotInteract.startMouseX) / slotInteract.slideWidth) * 100;
      const dyPct = ((e.clientY - slotInteract.startMouseY) / slotInteract.slideHeight) * 100;
      let nextX = slotInteract.startX;
      let nextY = slotInteract.startY;
      let nextW = slotInteract.startWidth;
      let nextH = slotInteract.startHeight;
      if (slotInteract.mode === 'drag') {
        nextX = Math.max(nextW / 2, Math.min(100 - nextW / 2, slotInteract.startX + dxPct));
        nextY = Math.max(nextH / 2, Math.min(100 - nextH / 2, slotInteract.startY + dyPct));
      } else {
        // Corner resize: opposite corner stays fixed.
        const signX = slotInteract.mode.includes('e') ? 1 : -1;
        const signY = slotInteract.mode.includes('s') ? 1 : -1;
        nextW = Math.max(MIN_W, Math.min(100, slotInteract.startWidth + signX * dxPct));
        nextH = Math.max(MIN_H, Math.min(100, slotInteract.startHeight + signY * dyPct));
        const fixedX = slotInteract.startX - signX * slotInteract.startWidth / 2;
        const fixedY = slotInteract.startY - signY * slotInteract.startHeight / 2;
        nextX = Math.max(nextW / 2, Math.min(100 - nextW / 2, fixedX + signX * nextW / 2));
        nextY = Math.max(nextH / 2, Math.min(100 - nextH / 2, fixedY + signY * nextH / 2));
      }
      updateElementContent({
        ...content,
        slots: slots.map((s) =>
          s.id === slotInteract.slotId ? { ...s, x: nextX, y: nextY, width: nextW, height: nextH } : s
        ),
      });
      if (!slotInteract.moved && (Math.abs(dxPct) > 0.2 || Math.abs(dyPct) > 0.2)) {
        setSlotInteract((s) => (s ? { ...s, moved: true } : s));
      }
    };
    const onUp = () => {
      if (slotInteract.moved) saveToHistory?.();
      setSlotInteract(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [slotInteract, content, slots, updateElementContent, saveToHistory]);

  const getLabelText = (labelId) => labels.find((l) => l.id === labelId)?.text || '';

  const renderLabelPill = (label, opts = {}) => {
    const {
      isDraggable = true,
      isInSlot = false,
      slotResult = null,
      slotId = null,
      fill = false,
    } = opts;

    let bg = labelColor;
    let textColor = labelTextColor;
    let borderColor = labelColor;
    if (isInSlot && showResult && slotResult !== null) {
      if (slotResult === true) {
        bg = '#dcfce7'; textColor = '#166534'; borderColor = '#22c55e';
      } else if (slotResult === false) {
        bg = '#fee2e2'; textColor = '#991b1b'; borderColor = '#ef4444';
      }
    }

    const dragEnabled = isDraggable && !disabled && !showResult && !isEditor;
    const touchDragEnabled = dragEnabled && !isInSlot;
    return (
      <div
        key={label.id + (isInSlot ? '_in_' + slotId : '_pal')}
        draggable={dragEnabled}
        onDragStart={(e) => handleDragStart(e, label.id)}
        onDragEnd={handleDragEnd}
        onPointerDown={touchDragEnabled ? (e) => handlePointerDown(e, label.id) : undefined}
        onClick={isInSlot ? () => handlePlacedClick(slotId) : undefined}
        className={`
          ${fill ? 'flex' : 'inline-flex'} items-center justify-center px-3 py-1.5 rounded-full
          text-sm font-medium border-2 ${fill ? 'text-center leading-tight' : 'whitespace-nowrap'}
          ${dragEnabled ? 'cursor-grab active:cursor-grabbing' : ''}
          ${isInSlot && !showResult && !disabled && !isEditor ? 'cursor-pointer' : ''}
          ${draggedLabelId === label.id ? 'opacity-40' : ''}
          transition-colors duration-100
        `}
        style={{
          backgroundColor: bg,
          color: textColor,
          borderColor,
          fontFamily,
          fontSize: `${fontSize}px`,
          ...(fill ? { width: '100%', height: '100%' } : {}),
          WebkitUserDrag: dragEnabled ? 'element' : 'none',
          WebkitUserSelect: 'none',
          userSelect: 'none',
          // Prevents the browser from claiming touch gestures (scroll/zoom)
          // before pointermove reaches us on mobile.
          touchAction: touchDragEnabled ? 'none' : undefined,
        }}
      >
        {label.text}
      </div>
    );
  };

  return (
    <div ref={rootRef} className="w-full h-full relative" style={{ pointerEvents: 'none' }}>
      {/* Floating palette — free x/y/width/height in % of the slide. In editor
          mode it can be dragged (body mousedown) and resized (corner handles).
          Stays visible after check so the surrounding layout doesn't shift. */}
      <div
        onPointerDown={isEditor ? (e) => beginPaletteInteract(e, 'drag') : undefined}
        className={`absolute rounded-lg border shadow-sm ${isEditor ? 'border-brand-primary cursor-move' : 'border-gray-200'}`}
        style={{
          left: `${palettePos.x}%`,
          top: `${palettePos.y}%`,
          width: `${paletteBoxSize.width}%`,
          height: `${paletteBoxSize.height}%`,
          backgroundColor: paletteColor,
          pointerEvents: 'auto',
        }}
      >
        {/* Inner scroll container so long label lists stay inside the palette
            while the resize handles below can render outside the box. */}
        <div
          className={`w-full h-full flex ${isHorizontalPalette ? 'flex-row flex-wrap' : 'flex-col'} items-center justify-center gap-2 p-2 overflow-auto`}
          style={{ pointerEvents: 'none' }}
        >
          {shuffledLabels.map((label) => {
            const isPlaced = !isEditor && placedLabelIds.has(label.id);
            return (
              <span
                key={label.id}
                style={{
                  visibility: isPlaced ? 'hidden' : 'visible',
                  pointerEvents: isPlaced ? 'none' : 'auto',
                }}
              >
                {renderLabelPill(label)}
              </span>
            );
          })}
          {shuffledLabels.length === 0 && (
            <span className="text-xs text-gray-400 italic px-2 whitespace-nowrap pointer-events-none">
              {t('games.dragLabel.addLabelsInSettings')}
            </span>
          )}
          {allPlaced && (
            <span className="absolute inset-0 flex items-center justify-center text-xs text-gray-400 pointer-events-none">
              {t('games.dragLabel.allLabelsPlaced')}
            </span>
          )}
        </div>
        {isEditor && updateElementContent && (
          <>
            {['nw', 'ne', 'sw', 'se'].map((corner) => (
              <div
                key={corner}
                onPointerDown={(e) => beginPaletteInteract(e, `resize-${corner}`)}
                className="absolute w-3 h-3 bg-white border-2 border-brand-primary rounded-sm shadow"
                style={{
                  top: corner.startsWith('n') ? -6 : 'auto',
                  bottom: corner.startsWith('s') ? -6 : 'auto',
                  left: corner.endsWith('w') ? -6 : 'auto',
                  right: corner.endsWith('e') ? -6 : 'auto',
                  cursor:
                    corner === 'nw' || corner === 'se' ? 'nwse-resize' : 'nesw-resize',
                  pointerEvents: 'auto',
                }}
              />
            ))}
          </>
        )}
      </div>

      {/* Slot layer — spans the whole slide. Root is transparent to pointer
          events so unrelated slide elements below can still be clicked; only
          the slots themselves are interactive drop targets. */}
      {slots.length === 0 && isEditor && (
        <div className="absolute top-2 right-2 flex items-center gap-1.5 text-gray-500 pointer-events-none bg-white/85 px-2 py-1 rounded shadow-sm">
          <MousePointerClick size={14} />
          <p className="text-xs">{t('games.dragLabel.addSlotsInSettings')}</p>
        </div>
      )}
      {slots.map((slot) => {
        const placedLabelId = placements[slot.id];
        const slotResult = showResult && perSlotResults ? perSlotResults[slot.id] : null;
        const isOver = dragOverSlotId === slot.id;
        return (
          <div
            key={slot.id}
            data-drag-slot-id={!isEditor && !showResult ? slot.id : undefined}
            onPointerDown={isEditor ? (e) => beginSlotInteract(e, 'drag', slot.id) : undefined}
            onDragOver={(e) => handleSlotDragOver(e, slot.id)}
            onDragLeave={handleSlotDragLeave}
            onDrop={(e) => handleSlotDrop(e, slot.id)}
            className={`absolute flex items-center justify-center rounded-full border-2 border-dashed transition-colors ${isEditor ? 'cursor-move' : ''}`}
            style={{
              left: `${slot.x}%`,
              top: `${slot.y}%`,
              width: `${slot.width || 18}%`,
              height: `${slot.height || 10}%`,
              minWidth: `${labelMinWidth}px`,
              transform: 'translate(-50%, -50%)',
              borderColor:
                slotResult === true ? '#22c55e'
                  : slotResult === false ? '#ef4444'
                  : slotColor,
              backgroundColor: isOver ? 'rgba(42, 77, 37, 0.12)' : 'rgba(255,255,255,0.6)',
              pointerEvents: 'auto',
            }}
          >
            {placedLabelId
              ? renderLabelPill(
                  labels.find((l) => l.id === placedLabelId) || { id: placedLabelId, text: getLabelText(placedLabelId) },
                  { isDraggable: false, isInSlot: true, slotResult, slotId: slot.id, fill: true }
                )
              : null}
            {isEditor && updateElementContent && ['nw', 'ne', 'sw', 'se'].map((corner) => (
              <div
                key={corner}
                onPointerDown={(e) => beginSlotInteract(e, `resize-${corner}`, slot.id)}
                className="absolute w-3 h-3 bg-white border-2 border-brand-primary rounded-sm shadow"
                style={{
                  top: corner.startsWith('n') ? -6 : 'auto',
                  bottom: corner.startsWith('s') ? -6 : 'auto',
                  left: corner.endsWith('w') ? -6 : 'auto',
                  right: corner.endsWith('e') ? -6 : 'auto',
                  cursor: corner === 'nw' || corner === 'se' ? 'nwse-resize' : 'nesw-resize',
                  pointerEvents: 'auto',
                }}
              />
            ))}
          </div>
        );
      })}

      {/* Action bar */}
      {!isEditor && (
        <div className="absolute bottom-2 right-2 z-10 flex items-center gap-2" style={{ pointerEvents: 'auto' }}>
          {showResult && allowRetry && perSlotResults && !Object.values(perSlotResults).every((v) => v === true) && (
            <button
              onClick={handleRetry}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg shadow-sm transition"
            >
              <RotateCcw size={14} />
              {t('games.common.tryAgain')}
            </button>
          )}
          {!showResult && !hideCheckButton && (
            <button
              onClick={handleCheck}
              disabled={!allSlotsFilled}
              className={`px-4 py-1.5 text-sm font-medium rounded-lg shadow-sm transition ${
                allSlotsFilled
                  ? 'bg-indigo-500 text-white hover:bg-indigo-600'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {t('games.common.check')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default DragLabelBlankElement;
